import React, { useState, useEffect, useCallback } from "react";
import { adminLiquidityApi } from "../../services/api";

/**
 * Tab Wallet Segnalati del pannello admin liquidità.
 */
const AdminLiquidityFlaggedWallets = ({ adminKey }) => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterReviewed, setFilterReviewed] = useState("false");
  const [reviewingWallet, setReviewingWallet] = useState(null);

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 50 };
      if (filterReviewed !== "") params.reviewed = filterReviewed;
      const data = await adminLiquidityApi.getFlaggedWallets(adminKey, params);
      setWallets(data.wallets || []);
    } catch (e) {
      setError(e.message || "Error loading flagged wallets");
    } finally {
      setLoading(false);
    }
  }, [adminKey, filterReviewed]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  const handleReview = async (wallet) => {
    if (!window.confirm(`Mark wallet ${wallet.wallet} as reviewed?`)) return;
    setReviewingWallet(wallet.wallet);
    try {
      await adminLiquidityApi.reviewWallet(adminKey, wallet.wallet, "admin");
      await fetchWallets();
    } catch (e) {
      alert("Errore: " + e.message);
    } finally {
      setReviewingWallet(null);
    }
  };

  return (
    <div style={s.container}>
      {/* Filtri */}
      <div style={s.filtersRow}>
        <select
          value={filterReviewed}
          onChange={(e) => setFilterReviewed(e.target.value)}
          style={s.select}
        >
          <option value="">All</option>
          <option value="false">Pending review</option>
          <option value="true">Already reviewed</option>
        </select>

        <button onClick={fetchWallets} style={s.btnRefresh}>
          🔄 Refresh
        </button>

        <span style={s.totalLabel}>{wallets.length} wallets</span>
      </div>

      {error && <div style={s.error}>⚠️ {error}</div>}

      {loading ? (
        <div style={s.loading}>Loading flagged wallets...</div>
      ) : wallets.length === 0 ? (
        <div style={s.empty}>✅ No flagged wallets to display.</div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Wallet</th>
                <th style={s.th}>Reason</th>
                <th style={s.th}>ETH</th>
                <th style={s.th}>Data flag</th>
                <th style={s.th}>Auto-revocato</th>
                <th style={s.th}>Revisione</th>
                <th style={s.th}>Azione</th>
              </tr>
            </thead>
            <tbody>
              {wallets.map((w) => (
                <tr key={w.wallet} style={{ backgroundColor: w.reviewed ? "#f7fafc" : "#fff5f5" }}>
                  <td style={{ ...s.td, fontFamily: "monospace", fontSize: 12 }}>
                    {`${w.wallet.slice(0, 10)}...${w.wallet.slice(-6)}`}
                  </td>
                  <td style={{ ...s.td, maxWidth: 260, fontSize: 12, color: "#c53030" }}>
                    {w.reason || "—"}
                  </td>
                  <td style={s.td}>
                    {w.eth_amount ? `${parseFloat(w.eth_amount).toFixed(4)} ETH` : "—"}
                  </td>
                  <td style={{ ...s.td, fontSize: 12 }}>
                    {w.flagged_at ? new Date(w.flagged_at).toLocaleString("en-US") : "—"}
                  </td>
                  <td style={{ ...s.td, textAlign: "center" }}>
                    {w.auto_revoked ? (
                      <span style={s.badgeOrange}>✓ Sì</span>
                    ) : (
                      <span style={s.badgeGray}>No</span>
                    )}
                  </td>
                  <td style={s.td}>
                    {w.reviewed ? (
                      <div>
                        <span style={s.badgeGreen}>✓ Reviewed</span>
                        {w.reviewed_at && (
                          <div style={{ fontSize: 11, color: "#718096", marginTop: 2 }}>
                            {new Date(w.reviewed_at).toLocaleString("en-US")}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={s.badgeRed}>Pending</span>
                    )}
                  </td>
                  <td style={s.td}>
                    {!w.reviewed && (
                      <button
                        onClick={() => handleReview(w)}
                        disabled={reviewingWallet === w.wallet}
                        style={{
                          ...s.btnSmall,
                          opacity: reviewingWallet === w.wallet ? 0.6 : 1,
                        }}
                      >
                        {reviewingWallet === w.wallet ? "..." : "✓ Mark reviewed"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: "0 4px" },
  filtersRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  select: {
    padding: "7px 10px",
    border: "1px solid #cbd5e0",
    borderRadius: 4,
    fontSize: 14,
    backgroundColor: "white",
    color: "#2c3e50",
  },
  btnRefresh: {
    padding: "7px 12px",
    backgroundColor: "#edf2f7",
    border: "1px solid #cbd5e0",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  totalLabel: { marginLeft: "auto", fontSize: 13, color: "#718096" },
  loading: { padding: 24, color: "#718096", textAlign: "center" },
  error: {
    padding: 12,
    color: "#c53030",
    backgroundColor: "#fff5f5",
    borderRadius: 6,
    marginBottom: 12,
  },
  empty: { padding: 24, color: "#38a169", textAlign: "center" },
  tableWrapper: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "white",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    fontSize: 14,
  },
  th: {
    padding: "10px 12px",
    backgroundColor: "#2c3e50",
    color: "white",
    fontWeight: 600,
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid #edf2f7",
    verticalAlign: "middle",
  },
  badgeGreen: {
    backgroundColor: "#c6f6d5",
    color: "#276749",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeRed: {
    backgroundColor: "#fed7d7",
    color: "#c53030",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeOrange: {
    backgroundColor: "#feebc8",
    color: "#c05621",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeGray: {
    backgroundColor: "#edf2f7",
    color: "#718096",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
  },
  btnSmall: {
    padding: "4px 10px",
    backgroundColor: "#3182ce",
    color: "white",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 500,
  },
};

export default AdminLiquidityFlaggedWallets;
